/**
 *  This is the top level component class that is responsible for managing the state of
 *  it's underlying sub-components
 */

// Libs
import React, {Component} from 'react';
import classnames from 'classnames';

// Functions
import {addRecentSearch, pruneLocalStorage} from './functions/recent';
import {keypress} from './functions/keyboard';
import {focusSearchInput, getHintSuggestion} from './functions/searchinput';
import {emitGE, emitSE, emitFocusEvent, emitStartEvent} from './functions/edap';
import {skipFilters} from './functions/filters';

// Data
import {TypeaheadStore} from './store';
import {UISTATES} from './enum';

// View components
import {Typeahead} from './components/typeahead';
import {AriaStatus} from './components/ariastatus';
import {SvgDefinitions} from './components/svgdefinitions';

// Style
import './container.less';

const PIDRegExp = /^(p\d+)/;

class TypeaheadContainer extends Component {
    /* istanbul ignore next */
    constructor(props) {
        super(props);

        // Default state
        // This is where we apply global context to the component
        this.state = {
            uistate: UISTATES.CLOSED,
            results: false,
            xhrPending: false,
            searchSuggestion: '',
            selectedDestination: '',
            selectedSubDestination: '',
            selectDestinationIndex: -1,
            selectDestinationSubIndex: -1,
            used: false,
            isTypeaheadInputFocus: false,
            ...this.props
        };

        this.data = {
            backspaceCounter: 0,
            userInputLength: 0,
            noSelectionCounter: 0,
            prevsearch: null,
            resultsUuid: null,
            selectionDepth: 1 // 1-based depth
        };

        this.localState = {
            isTypeaheadFocused: false,
            wasTrendingFetched: false
        };
    }

    componentDidMount() {
        // Initialize the typeahead store /w initial state as configuration
        this.store = new TypeaheadStore(this.props.config);

        // Remove old/invalid entries from localStorage
        pruneLocalStorage();

        // If value is set, pre-warm typeahead results
        if (this.state.search && this.state.search.length > 0) {
            this.data.prevsearch = this.state.search;
            this.store.getResults(
                this.state.search,
                this.props.filterType,
                () => {}
            );
        }

        // add window click handler to close pop ups
        window.addEventListener(
            'click',
            this.goToClosedStateIfClickedOutside,
            true
        );
        window.addEventListener(
            'touchstart',
            this.goToClosedStateIfClickedOutside,
            true
        );
    }

    /**
     * componentWillReceiveProps()
     * @param {*} nextProps
     */
    componentWillReceiveProps(nextProps) {
        // Sync filtered list of new props to sync with state.
        let nextState = {};
        /* istanbul ignore next */
        Object.keys(nextProps).forEach((prop) => {
            if (
                nextProps[prop] !== this.props[prop] &&
                nextProps[prop] !== this.state[prop]
            ) {
                let nextStateValue = nextProps[prop];
                if (prop === 'search') {
                    // If parent context updates "search", invalidate selectedDestination in order to render search text.
                    this.setState({
                        selectedDestination: false,
                        results: false
                    });
                }
                nextState[prop] = nextStateValue;
            }
        });
        if (Object.keys(nextState).length > 0) {
            this.setState({...nextState});
            return true;
        }
        return false;
    }

    /**
     * componentWillUpdate
     * @param {*} nextProps
     * @param {*} nextState
     */
    componentWillUpdate(nextProps, nextState) {
        // Pre-warm trending options when showTrendingSuggestions is set by livelab.
        // Race condition fix when typeahead in focus but 'showTrendingSuggestions' option is not available yet.
        // TODO: move this in componentDidMount when trending options A/B test is over.
        if (
            this.props.showTrendingSuggestions &&
            !this.localState.wasTrendingFetched
        ) {
            this.store.getTrending(() => {
                this.localState.wasTrendingFetched = true;
                if (this.localState.isTypeaheadFocused) {
                    this.handleFocus();
                }
            });
        }

        this.incrementNoSelectionCounter(nextState.results);

        if (
            this.state.uistate !== UISTATES.CLOSED &&
            nextState.uistate === UISTATES.CLOSED
        ) {
            this.props.onClose(nextState.search || this.state.search); // eslint-disable-line no-unused-expressions
        }
        if (
            this.state.search !== nextState.search ||
            this.state.selectedDestination !== nextState.selectedDestination
        ) {
            // Invoke `onUpdate()` callback
            this.props.onUpdate(nextState);
        }
    }

    /**
     * incrementNoSelectionCounter()
     * Increments noSelectionCounter if results list was updated with new suggestions
     */
    incrementNoSelectionCounter = (resultsNext) => {
        let results = this.state.results;
        if (
            !results ||
            !resultsNext ||
            (results.length === 0 && resultsNext.length === 0)
        ) {
            return;
        }

        if (results.length !== resultsNext.length) {
            this.data.noSelectionCounter++;
            return;
        }

        let isResultsChanged = resultsNext.find((element, index) => {
            return !results[index] || results[index].uuid !== element.uuid;
        });

        if (isResultsChanged) {
            this.data.noSelectionCounter++;
        }
    };

    /**
     * handleError()
     * Handle errors in child components
     */
    handleError = (msg) => {
        console.error(msg); //eslint-disable-line
        return emitGE({
            eventaction: 'error',
            eventvalue: msg,
            eventlabel: ''
        });
    };

    /**
     *  handleTopLevelKeyEvents()
     *  Handle top level key events to implement keyboard navigation
     */
    handleTopLevelKeyEvents = (ev) => {
        // Dispatch keyboard event
        const result = keypress(this.state, ev);

        // true: keypress() resulted in selected destination
        if (result === true) {
            let selectedIndex =
                this.state.selectDestinationIndex === -1
                    ? 0
                    : this.state.selectDestinationIndex;
            const selectedDestination = this.state.results[selectedIndex];
            this.data.selectionDepth = selectedIndex + 1; // 1-based selectionDepth
            let selectedSubDestination = null;
            if (this.state.selectDestinationSubIndex > -1) {
                selectedSubDestination =
                    selectedDestination.subSuggestions[
                        this.state.selectDestinationSubIndex
                    ];
            }
            this.handleSelectDestination(
                selectedDestination,
                selectedSubDestination
            );
            setTimeout(() => {
                this.containerEl.querySelector('input.control').blur();
            }, 1);
        } else if (result) {
            // control key press ESC, Up, Down
            this.setState(result);
            this.handleFocus();
        } else {
            // char key press
            this.triggerStartEdap();
        }
        return result;
    };

    /**
     * handleOnMouseOver()
     * Handle on mouse over to reset keyboard navigation state
     */
    handleOnMouseOver = () => {
        if (this.state.selectDestinationIndex !== -1) {
            this.setState({selectDestinationIndex: -1});
        }
        if (this.state.uistate !== UISTATES.CLOSED) {
            this.handleFocus();
            return true;
        }
        return false;
    };

    /**
     * goToClosedStateIfClickedOutside()
     * if click outside of component close any open popups
     */
    goToClosedStateIfClickedOutside = (e) => {
        if (this.state.uistate !== UISTATES.CLOSED) {
            if (this.containerEl && !this.containerEl.contains(e.target)) {
                this.checkUserInputOnClickOutside();
                this.close();
                return true;
            }
        }
        return false;
    };

    checkUserInputOnClickOutside = () => {
        let state = this.state;
        let isEmptyOrDifferentSelectedDestination =
            !state.selectedDestination ||
            !state.selectedDestination.term ||
            state.selectedDestination.term.toLowerCase() !==
                state.search.toLowerCase();

        if (state.search && isEmptyOrDifferentSelectedDestination) {
            this.handleSelectDestination(state.search);
        }
    };

    /**
     * handleTypeaheadClick()
     */
    handleTypeaheadClick = () => {
        this.setState({
            uistate: UISTATES.SELECT_DESTINATION
        });
    };

    /**
     * handleFocus()
     * Handles when input receives focus
     */
    handleFocus = (e) => {
        const state = focusSearchInput(this.state);
        this.localState.isTypeaheadFocused = true;
        if (e && e.currentTarget) {
            const target = e.currentTarget;
            // setTimeout is needed because some browsers eg. MS Edge are unable to select synchronously
            setTimeout(() => target.select(), 50);
        }

        // If user makes focus and search input is empty then show trending results
        if (!this.state.search && this.props.showTrendingSuggestions) {
            this.store.getTrending((trendingResults) => {
                this.setState({
                    results: trendingResults.resultsWithHighlights,
                    isTrendingResults: true
                });
            });
        }

        if (state) {
            // If results is not initialized, fetch em
            this.state.uistate = state.uistate; //eslint-disable-line
            if (this.state.results === false) {
                this.handleSearchInputChange();
            }

            // Make sure to update to open state
            this.setState(state);
        }

        /* istanbul ignore next */
        if (this.state.isTypeaheadInputFocus) {
            this.setState({isTypeaheadInputFocus: false});
        }
        this.triggerFocusEdap();
        this.props.onFocus();
    };

    triggerFocusEdap() {
        if (!this.state.wasFirstFocus) {
            this.setState({wasFirstFocus: true});
            emitFocusEvent(this.state.search || null);
        }
    }

    triggerStartEdap() {
        if (!this.state.wasFirstInput) {
            this.setState({wasFirstInput: true});
            emitStartEvent(this.state.search || null);
        }
    }

    getRecentDisplayedNumber = (results) => {
        return results.reduce((recentsNumber, result) => {
            if (result.recentlyviewed) {
                recentsNumber++;
            }
            return recentsNumber;
        }, 0);
    };

    getNonStandardDisplayedNumber = (results) => {
        const nonStandardResult = results.find((result) => {
            return result.subSuggestions;
        });
        return nonStandardResult ? nonStandardResult.subSuggestions.length : 0;
    };

    /**
     * handleSearchInputChange()
     * Destination Input Change event handler
     */
    handleSearchInputChange = (ev) => {
        let userInput = (ev ? ev.target.value : this.state.search) || '';
        let firstResultTerm =
            this.state.results &&
            this.state.results[0] &&
            this.state.results[0].term;
        const {showInlineSuggestion} = this.props;

        // Increment "backspace" counter if user delete character
        if (userInput.length < this.data.userInputLength) {
            this.data.backspaceCounter++;
        }

        this.data.userInputLength = userInput.length;

        // Trim preceding whitespace
        userInput = userInput.replace(/^\s(.*)/, '$1');

        // Emit a `used` event (one per pageview)
        if (userInput.length && !this.state.used) {
            emitGE({eventaction: 'used'});
            this.setState({used: true});
        }

        // If userInput is empty, set an empty state
        if (!userInput || userInput.length === 0 || PIDRegExp.test(userInput)) {
            if (this.props.showTrendingSuggestions) {
                this.store.getTrending((trendingResults) => {
                    this.setState({
                        results: trendingResults.resultsWithHighlights,
                        search: '',
                        searchSuggestion: '',
                        selectedDestination: false,
                        isTrendingResults: true
                    });
                });
                return false;
            }
            this.setState({
                results: false,
                search: userInput,
                searchSuggestion: '',
                selectedDestination: false
            });
            this.store.getResults(false); // Send signal to store that user has changed input
            return false;
        }
        this.setState({
            search: userInput,
            searchSuggestion: showInlineSuggestion
                ? getHintSuggestion(userInput, firstResultTerm)
                : '',
            uistate: UISTATES.SELECT_DESTINATION,
            xhrPending: true
        });

        // fetch results from store (either via XHR or cache)
        this.store.getResults(
            userInput,
            this.props.filterType,
            (searchResults) => {
                // Update post-fetch state
                firstResultTerm =
                    searchResults.results[0] && searchResults.results[0].term;
                this.setState({
                    searchSuggestion: showInlineSuggestion
                        ? getHintSuggestion(userInput, firstResultTerm)
                        : '',
                    results: searchResults.resultsWithHighlights,
                    poweredByGoogle: searchResults.poweredByGoogle,
                    xhrPending: false,
                    isTrendingResults: false
                });
                this.data.resultsUuid = searchResults.resultsUuid;
            }
        );
        return true;
    };

    /**
     * close()
     * Transition to closed state
     */
    close = () => {
        // forces typeahead to lose focus
        window.document.activeElement.blur();
        this.data.isTypeaheadFocused = false;
        this.setState({
            uistate: UISTATES.CLOSED
        });
    };

    /**
     * handleSelectDestination()
     * Update destination state
     */
    handleSelectDestination = (
        _selectedDestination,
        _selectedSubDestination
    ) => {
        // Optimize function
        if (this.state.uistate !== UISTATES.SELECT_DESTINATION) {
            return false;
        }

        let selectedDestination = {..._selectedDestination};
        delete selectedDestination.highlightedTerm;

        const selectedSubDestination = {..._selectedSubDestination};
        const filterId =
            selectedSubDestination &&
            selectedSubDestination.filter &&
            selectedSubDestination.filter.id;
        const resultsUuid = this.data.resultsUuid;

        let totalResults = 0;
        let nonStandardDisplayed = 0;
        let recentsDisplayed = 0;

        if (this.state.results) {
            recentsDisplayed = this.getRecentDisplayedNumber(
                this.state.results
            );
            nonStandardDisplayed = this.getNonStandardDisplayedNumber(
                this.state.results
            );
            totalResults = this.state.results.length - recentsDisplayed;
        }

        let selectionType = 'standard';
        if (selectedDestination.poweredByGoogle) {
            selectionType = 'google';
            totalResults = 0; // google is not standard results so totalResults is 0
        } else if (filterId) {
            selectionType = 'filter';
        }

        if (!selectedDestination.uuid) {
            // If user has no input, cancel selection
            if (!this.state.search) {
                return false;
            }
            selectedDestination = {
                term: this.state.search
            };
            emitGE({
                eventaction: 'selected-destination-with-no-results',
                eventlabel: null,
                eventvalue: selectedDestination.term
            });
        } else {
            let selectionData = {
                userinput: this.state.search,
                totalbackspaces: this.data.backspaceCounter,
                totalnoselections: this.data.noSelectionCounter,
                totalresults: totalResults,
                selectiondepth:
                    selectedDestination.suggestionDepth ||
                    this.data.selectionDepth,
                selectiontype: selectionType,
                nonstandarddisplayed: nonStandardDisplayed,
                recentsdisplayed: recentsDisplayed,
                prevsearch: this.data.prevsearch,
                geouuid: selectedDestination.uuid,
                geotypes: selectedDestination.type,
                geoname: selectedDestination.term,
                matchtype: selectedDestination.matchType,
                ...(resultsUuid ? {resultsuuid: resultsUuid} : {}),
                ...(filterId ? {filterid: filterId} : {}),
                ...(selectionType === 'google'
                    ? {googleselectionplaceid: selectedDestination.uuid}
                    : {})
            };
            emitSE(selectionData);
        }

        // reset "backspace" and "noSelection" counter after selection
        this.data = {
            ...this.data,
            backspaceCounter: 0,
            noSelectionCounter: 0,
            prevsearch: selectedDestination.term
        };

        let updateState = {
            searchSuggestion: '',
            selectedDestination,
            selectedSubDestination,
            selectDestinationIndex: -1,
            selectDestinationSubIndex: -1
        };

        if (this.state.retainUserInput === true) {
            updateState.search = selectedDestination.term;
        }

        // Add destination to recently searched except PID and destination created from custom user input
        if (!PIDRegExp.test(this.state.search) && selectedDestination.uuid) {
            addRecentSearch(selectedDestination);
        }

        // Updated state
        this.setState(updateState, () => {
            // Invoke onSelectDestination() callback
            this.props.onSelectDestination(
                selectedDestination,
                selectedSubDestination
            );
            this.close();
        });

        return true;
    };

    _getSelectedTermFromState = () => {
        const {selectedDestination} = this.state;
        return (selectedDestination && selectedDestination.term) || '';
    };

    _getSelectedFilterNameFromState = () => {
        const {selectedSubDestination} = this.state;
        return (
            selectedSubDestination &&
            selectedSubDestination.filter &&
            selectedSubDestination.filter.name
        );
    };

    /**
     * getInputDisplayTerm()
     * Helper method to get search term for destination input
     */
    getInputDisplayTerm() {
        if (this.state.uistate === UISTATES.SELECT_DESTINATION) {
            return this.state.search;
        }

        const term = this._getSelectedTermFromState();
        const filter = this._getSelectedFilterNameFromState();

        let displayTerm = '';
        if (term) {
            if (!this.props.hideSelectedFilter && filter) {
                displayTerm = `${term} > ${filter}`;
            } else {
                displayTerm = term;
            }
        }
        return displayTerm;
    }

    /**
     * handleClear()
     * Clears user search
     */
    handleClear = () => {
        this.setState({
            search: '',
            searchSuggestion: '',
            selectedDestination: false,
            isTypeaheadInputFocus: true,
            results: false,
            poweredByGoogle: false
        });
    };

    /**
     * createContainerRef()
     * Create reference to container element for purpose of animation, etc.
     */
    createContainerRef = (el) => {
        this.containerEl = el;
    };

    /**
     * render()
     * Render the component
     */
    render() {
        return (
            <div
                role="presentation"
                onKeyDown={this.handleTopLevelKeyEvents}
                onMouseOver={this.handleOnMouseOver}
                className={classnames(
                    'react-destination-typeahead',
                    this.state.uistate,
                    {
                        'react-destination-typeahead--no-icon': !this.state.icon
                    }
                )}
                ref={this.createContainerRef}
            >
                <SvgDefinitions />
                <AriaStatus {...this.props} {...this.state} />
                <Typeahead
                    {...this.props}
                    {...this.state}
                    onFocus={this.handleFocus}
                    onClick={this.handleTypeaheadClick}
                    onClear={this.handleClear}
                    onChange={this.handleSearchInputChange}
                    onSelectDestination={this.handleSelectDestination}
                    onError={this.handleError}
                    displayTerm={this.getInputDisplayTerm()}
                    rootElem={this.containerEl}
                    isMobile={this.props.isMobile}
                    onBlur={this.props.onBlur}
                    isTypeaheadInputFocus={
                        this.props.isTypeaheadInputFocus ||
                        this.state.isTypeaheadInputFocus
                    }
                    showSpinner={this.props.showSpinner}
                    showResultIcons={this.props.showResultIcons}
                    showResultLabels={this.props.showResultLabels}
                    skipFilters={skipFilters(this.props.filterType)}
                    filterType={this.props.filterType}
                />
            </div>
        );
    }
}

export default TypeaheadContainer;
export {TypeaheadContainer};
